import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface AppAndSharedTranslateLoaderOptions {
  sharedPrefix?: string;
  appPrefix?: string;
  suffix?: string;
  sharedDomains?: string[];
  appDomains?: string[];
}

export class AppAndSharedTranslateLoader implements TranslateLoader {
  private sharedPrefix: string;
  private appPrefix: string;
  private suffix: string;
  private sharedDomains: string[];
  private appDomains: string[];

  constructor(
    private http: HttpClient,
    options: AppAndSharedTranslateLoaderOptions = {},
  ) {
    this.sharedPrefix = options.sharedPrefix ?? './assets/i18n/shared/';
    this.appPrefix = options.appPrefix ?? './assets/i18n/';
    this.suffix = options.suffix ?? '.json';
    this.sharedDomains = options.sharedDomains ?? [];
    this.appDomains = options.appDomains ?? [];
  }

  public getTranslation(lang: string): Observable<Record<string, any>> {
    const sources$ = [
      this.loadTranslationFile(`${this.sharedPrefix}${lang}${this.suffix}`),
      ...this.sharedDomains.map((domain) =>
        this.loadTranslationFile(`${this.sharedPrefix}${domain}/${lang}${this.suffix}`),
      ),
      this.loadTranslationFile(`${this.appPrefix}${lang}${this.suffix}`),
      ...this.appDomains.map((domain) =>
        this.loadTranslationFile(`${this.appPrefix}${domain}/${lang}${this.suffix}`),
      ),
    ];

    return forkJoin(sources$).pipe(
      map((translations) => translations.reduce<Record<string, any>>(
        (merged, translation) => deepMerge(merged, translation),
        {},
      )),
    );
  }

  private loadTranslationFile(path: string): Observable<Record<string, any>> {
    return this.http
      .get<Record<string, any>>(path)
      .pipe(catchError(() => of({})));
  }
}

function deepMerge(a: any, b: any): any {
  if (!isPlainObject(a)) {
    return b;
  }
  if (!isPlainObject(b)) {
    return a;
  }

  const result: Record<string, any> = { ...a };
  for (const key of Object.keys(b)) {
    const aValue = (a as any)[key];
    const bValue = (b as any)[key];
    result[key] = deepMerge(aValue, bValue);
  }
  return result;
}

function isPlainObject(value: any): value is Record<string, any> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
